// i18n pilot infra (Stage 1, 2026-09-23). ICU via i18next-icu, react-i18next.
// (2026-10-11) Login raw-key fix: namespace loading is now resilient and
// observable. Previously ensureLanguage() loaded all namespaces in a single
// Promise.all and setLanguage() awaited it before changeLanguage(); one
// rejected dynamic import (flaky network, SW cache miss, deploy mid-load)
// rejected the whole chain, the void'ed bootstrap promise died silently, and
// no languageChanged event ever fired — so pages using the not-yet-registered
// namespace (e.g. LoginPage -> 'auth') rendered raw keys forever while
// namespaces that had already been added (e.g. 'common' on the landing page)
// kept working. Now each namespace load is isolated (allSettled + per-file
// retry), bundles are registered as they arrive, changeLanguage runs
// immediately (i18next re-resolves once bundles register), and every
// registration emits 'languageChanged' so mounted react-i18next consumers
// re-render without any remount.
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import ICU from 'i18next-icu';

export const NAMESPACES = ['common', 'auth', 'messages'] as const;
export type Namespace = (typeof NAMESPACES)[number];

const FALLBACK_LNG: Record<string, readonly string[]> = {
  'es-VE': ['es-MX'],
  'es-CO': ['es-MX'],
  'es-AR': ['es-MX'],
  'es-ES': ['es-MX'],
  'es': ['es-MX'],
  default: ['es-MX'],
};

let initialized = false;
function baseInit(): void {
  if (initialized) return;
  i18next.use(initReactI18next).use(ICU).init({
    lng: 'es-MX',
    fallbackLng: FALLBACK_LNG,
    ns: [...NAMESPACES],
    defaultNS: 'common',
    partialBundledLanguages: true,
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
    parseMissingKeyHandler: (key: string) => {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn(`[i18n] missing key: ${key}`);
      }
      // (2026-09-24) Never leak the missing-key warning glyph into prod UI
      // (F-16: sidebar group heading rendered "⚠Finanzas"). The dev console
      // warn above is the indicator; prod renders the raw key.
      return import.meta.env.DEV ? `⚠${key}` : key;
    },
  });
  initialized = true;
}

// Vite dynamic-import map over all locale json files (lazy).
const localeFiles = import.meta.glob<{ default: Record<string, unknown> }>(
  '../locales/*/*.json',
);

/** Locales whose bundles are needed to render `locale` (primary first). */
export function localesFor(locale: string): string[] {
  if (locale === 'en') return ['en'];
  if (locale === 'es-MX') return ['es-MX'];
  if (locale.startsWith('es')) return [locale, 'es-MX'];
  return ['es-MX'];
}

const IMPORT_RETRIES = 2;

/** Dynamic import with bounded retries (transient network/SW cache misses). */
async function loadLocaleModule(
  loader: () => Promise<{ default: Record<string, unknown> }>,
): Promise<{ default: Record<string, unknown> }> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= IMPORT_RETRIES; attempt++) {
    try {
      return await loader();
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError;
}

/** Register one namespace bundle; emits languageChanged so mounted consumers re-render. */
function registerBundle(lng: string, ns: string, data: Record<string, unknown>): void {
  if (i18next.hasResourceBundle(lng, ns)) return;
  i18next.addResourceBundle(lng, ns, data, true, true);
  // react-i18next subscribes to languageChanged; re-emitting after a late
  // bundle registration re-renders mounted useTranslation() consumers (the
  // login page) without any remount.
  i18next.emit('languageChanged', i18next.language);
}

/**
 * Dynamic-import ../locales/<needed>/<ns>.json for namespaces common/auth/messages.
 * (2026-10-11) Each namespace load is isolated: one failing chunk no longer
 * prevents the other namespaces from registering (the old all-or-nothing
 * Promise.all left e.g. 'auth' unregistered and the login page rendered raw
 * keys). Resolves with the list of namespaces that failed to load.
 */
export async function ensureLanguage(locale: string): Promise<Namespace[]> {
  baseInit();
  const targets = localesFor(locale);
  const jobs = targets.flatMap((lng) =>
    NAMESPACES.map((ns) => ({
      ns,
      run: (async () => {
        const loader = localeFiles[`../locales/${lng}/${ns}.json`];
        if (!loader) return;
        const data = (await loadLocaleModule(loader)).default;
        registerBundle(lng, ns, data);
      })(),
    })),
  );
  const results = await Promise.allSettled(jobs.map((j) => j.run));
  const failed = new Set<Namespace>();
  jobs.forEach((j, i) => {
    if (results[i].status === 'rejected') {
      failed.add(j.ns);
      if (import.meta.env.DEV) {
        console.warn('[i18n] locale chunk failed to load:', (results[i] as PromiseRejectedResult).reason);
      }
    }
  });
  return [...failed];
}

/** ensureLanguage + i18next.changeLanguage; returns the resolved locale. */
export async function setLanguage(locale: string): Promise<string> {
  baseInit();
  const targets = localesFor(locale);
  const resolved = targets[0];
  // changeLanguage first (with partialBundledLanguages this resolves even
  // before every bundle exists), then load/register bundles. Any late
  // registration emits languageChanged, so mounted consumers re-render.
  await i18next.changeLanguage(resolved);
  const failed = await ensureLanguage(locale);
  if (failed.length > 0) {
    // Surface loud in dev/QA, keep translations best-effort in prod.
    if (import.meta.env.DEV) {
      console.warn(`[i18n] setLanguage('${locale}') finished with failed namespaces: ${failed.join(', ')}`);
    }
  }
  return resolved;
}

/** localStorage 'v-lang' || ('en' if navigator.language starts with 'en') || 'es-MX'. */
export function getInitialLocale(): string {
  try {
    const stored = localStorage.getItem('v-lang');
    if (stored) return stored;
  } catch {
    /* SSR/private-mode: ignore */
  }
  if (typeof navigator !== 'undefined' && navigator.language?.startsWith('en')) {
    return 'en';
  }
  return 'es-MX';
}
